/*
 * MemoryPersistence — Client-API zum manuellen User-Memory-Editor (Spec §3, §8).
 *
 * Verantwortlichkeit:
 *   - Lock acquiren / heartbeaten / freigeben (Spec §8.3, 60s TTL, 30s Heartbeat)
 *   - Memory laden (GET) und speichern (PUT) mit 1.5s Debounce (Spec §8.1)
 *   - Cap-Violation als strukturierter Fehler durchreichen (Spec §8.2)
 *   - flushNow() fuer Editor-Close (synchroner letzter Save vor Lock-Release)
 *
 * Stateful pro Instanz — der MemoryEditor erzeugt EINE Instanz pro open()-Call
 * und ruft im close() flushNow() + unlock(). Bei Lock-Verlust (Heartbeat-409)
 * informieren wir den Caller via `onLockLost`-Callback; der MemoryEditor
 * schliesst dann den Pane mit Hinweis.
 */

import { handleSessionLost } from '../auth/AuthService.js'

const SAVE_DEBOUNCE_MS = 1500
// 20 s Heartbeat gegen 90 s Server-TTL (4.5x Puffer). Frueher 30 s/60 s, aber
// Background-Tab-Throttling kann setInterval-Ticks auf 1 Hz oder seltener
// drosseln — der dichtere Heartbeat-Takt + groessere TTL geben dem Browser
// genug Spielraum, ohne dass ein abgestuerzter Tab den Agent lange blockiert.
const HEARTBEAT_INTERVAL_MS = 20_000

export class MemoryPersistence {
    /**
     * @param {object} [opts]
     * @param {Function} [opts.fetch] - Override fuer Tests (default global fetch)
     * @param {Function} [opts.onLockLost] - Wird gerufen, wenn ein Heartbeat 409
     *   bekommt (anderer Tab hat das Lock geklaut, was bei hartem Lock nicht
     *   passieren sollte, aber Defense-in-Depth)
     * @param {Function} [opts.onSaveError] - (err) => void bei misslungenen Saves
     */
    constructor(opts = {}) {
        this._fetch = opts.fetch || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null)
        this._onLockLost = typeof opts.onLockLost === 'function' ? opts.onLockLost : null
        this._onSaveError = typeof opts.onSaveError === 'function' ? opts.onSaveError : null

        this._lockToken = null
        this._heartbeatTimer = null
        this._visibilityHandler = null

        this._saveTimer = null
        this._pendingText = null
        this._lastSavedText = null
        this._inFlightSave = null   // Promise des laufenden _doSave-Calls
    }

    // ─── Lock-Lifecycle ────────────────────────────────────────────────────

    /**
     * Acquire-Pfad. Gibt den Token zurueck oder wirft einen Error mit
     * `code: 'memory_locked'` wenn ein anderer Tab das Lock haelt.
     * Startet bei Erfolg den Heartbeat-Timer.
     */
    async lock() {
        const res = await this._fetch('/api/user/memory/lock', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({})
        })
        if (res.status === 401) {
            handleSessionLost()
            throw makeError('not_authenticated', 'Nicht eingeloggt')
        }
        if (res.status === 409) {
            throw makeError('memory_locked', 'Memory wird in einem anderen Tab editiert')
        }
        if (!res.ok) {
            throw makeError('lock_failed', `Lock acquire failed: ${res.status}`)
        }
        const data = await res.json()
        this._lockToken = data.lockToken
        this._startHeartbeat()
        return this._lockToken
    }

    /**
     * Heartbeat-Pfad. Wird vom Timer gerufen, kann auch manuell genutzt werden.
     * Liefert true bei Erfolg, false bei Lock-Verlust.
     */
    async heartbeat() {
        if (!this._lockToken) return false
        try {
            const res = await this._fetch('/api/user/memory/lock', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lockToken: this._lockToken })
            })
            if (res.status === 409) {
                this._handleLockLost()
                return false
            }
            if (res.status === 401) {
                handleSessionLost()
                return false
            }
            return res.ok
        } catch (err) {
            // Netzwerkfehler — wir verlieren das Lock NICHT sofort (TTL gibt
            // dem Browser eine Chance, die Verbindung wiederzubekommen).
            // Naechster Heartbeat (in 30s) versucht es erneut.
            console.warn('Memory-Lock heartbeat failed:', err.message)
            return false
        }
    }

    /**
     * Gibt das Lock frei und stoppt den Heartbeat-Timer. Idempotent — der
     * Server-DELETE ist auch ohne validen Token harmlos.
     */
    async unlock() {
        this._stopHeartbeat()
        const token = this._lockToken
        this._lockToken = null
        if (!token) return
        try {
            await this._fetch('/api/user/memory/lock', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lockToken: token })
            })
        } catch (err) {
            // Server-seitig laeuft das Lock spaetestens nach 60s ab.
            console.warn('Memory-Lock release failed:', err.message)
        }
    }

    // ─── Memory-IO ─────────────────────────────────────────────────────────

    /**
     * Liefert den aktuellen Memory-Inhalt vom Server.
     * @returns {Promise<{memory: string, byteLength: number, maxBytes: number}>}
     */
    async load() {
        const res = await this._fetch('/api/user/memory')
        if (res.status === 401) {
            handleSessionLost()
            throw makeError('not_authenticated', 'Nicht eingeloggt')
        }
        if (!res.ok) {
            throw makeError('load_failed', `Memory laden fehlgeschlagen: ${res.status}`)
        }
        const data = await res.json()
        // Cache fuer den Debounce-Vergleich — sonst feuert der erste
        // scheduleSave() einen Save fuer unveraenderten Inhalt.
        this._lastSavedText = data.memory || ''
        return data
    }

    /**
     * Plant einen Save in 1.5s. Aufeinanderfolgende Calls verlaengern den
     * Debounce (klassisches Trailing-Debounce).
     *
     * Skip-Filter (Single-Source-of-Truth fuer "wurde gespeichert"):
     *   - Server hat schon diesen exakten Stand (`text === _lastSavedText`)
     *     UND nichts ist in der Pipeline → nichts zu tun.
     *   - Identischer Text steht bereits im Pending-Slot → Timer NICHT zuruecksetzen
     *     (sonst feuert der Render-Loop den Debounce ewig weiter, ohne dass
     *     `_doSave` jemals laeuft).
     * Bei Save-Fehler bleibt `_lastSavedText` was es war → der Editor kann mit
     * exakt demselben Text einen erneuten Versuch ausloesen.
     *
     * @param {string} text
     */
    scheduleSave(text) {
        if (text === this._lastSavedText && this._pendingText === null && !this._inFlightSave) {
            return
        }
        if (text === this._pendingText) {
            return
        }
        this._pendingText = text
        if (this._saveTimer) clearTimeout(this._saveTimer)
        this._saveTimer = setTimeout(() => {
            this._saveTimer = null
            this._doSave().catch(err => this._handleSaveError(err))
        }, SAVE_DEBOUNCE_MS)
    }

    /**
     * Synchroner letzter Save: cancelt den Debounce, schreibt sofort wenn
     * pending. Wird vom MemoryEditor.close() awaited.
     */
    async flushNow() {
        if (this._saveTimer) {
            clearTimeout(this._saveTimer)
            this._saveTimer = null
        }
        // Wenn schon ein Save in-flight ist: warten, dann ggf. einen weiteren
        // schreiben (falls in der Zwischenzeit neue Aenderungen kamen).
        if (this._inFlightSave) {
            try { await this._inFlightSave } catch { /* error wurde bereits gemeldet */ }
        }
        if (this._pendingText !== null && this._pendingText !== this._lastSavedText) {
            await this._doSave()
        }
    }

    /**
     * Aktueller Lock-Token (fuer Debug / Tests).
     */
    getLockToken() {
        return this._lockToken
    }

    // ─── Interne Helfer ────────────────────────────────────────────────────

    async _doSave() {
        if (this._pendingText === null) return
        if (!this._lockToken) {
            // Kein Lock → kein Save. Sollte nur passieren, wenn nach Lock-Lost
            // weitergetippt wurde — der Caller wird dann ohnehin geschlossen.
            this._pendingText = null
            return
        }
        const text = this._pendingText
        // _pendingText sofort clearen — bei Erfolg ist es konsumiert, bei Fehler
        // soll der Caller mit demselben Text einen Retry triggern koennen
        // (sonst blockiert der scheduleSave-Filter "text === _pendingText").
        this._pendingText = null
        const promise = this._fetch('/api/user/memory', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ memory: text, lockToken: this._lockToken })
        }).then(async res => {
            if (res.status === 401) {
                handleSessionLost()
                throw makeError('not_authenticated', 'Nicht eingeloggt')
            }
            if (res.status === 409) {
                this._handleLockLost()
                throw makeError('memory_locked', 'Lock verloren')
            }
            if (res.status === 413) {
                const data = await res.json().catch(() => ({}))
                throw makeError('memory_too_large', 'Memory ueber Limit', { maxBytes: data.maxBytes })
            }
            if (!res.ok) {
                throw makeError('save_failed', `Save fehlgeschlagen: ${res.status}`)
            }
            this._lastSavedText = text
            return res.json()
        })
        this._inFlightSave = promise
        try {
            await promise
        } finally {
            this._inFlightSave = null
        }
    }

    _startHeartbeat() {
        this._stopHeartbeat()
        this._heartbeatTimer = setInterval(() => {
            this.heartbeat().catch(() => { /* heartbeat schluckt selbst */ })
        }, HEARTBEAT_INTERVAL_MS)
        // Im Browser wuerde unref() nicht existieren — Defense gegen
        // Test-Environments mit Node-Timers.
        if (this._heartbeatTimer && typeof this._heartbeatTimer.unref === 'function') {
            this._heartbeatTimer.unref()
        }
        // visibilitychange: wenn der Tab nach einer Hintergrund-Phase wieder
        // sichtbar wird, sofort heartbeaten — der Timer kann waehrenddessen
        // gedrosselt gewesen sein (Browsers throttlen setInterval auf 1 Hz oder
        // seltener in Background-Tabs). Sofortiger Refresh schliesst die Luecke.
        if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
            this._visibilityHandler = () => {
                if (document.visibilityState === 'visible' && this._lockToken) {
                    this.heartbeat().catch(() => { /* schluckt selbst */ })
                }
            }
            document.addEventListener('visibilitychange', this._visibilityHandler)
        }
    }

    _stopHeartbeat() {
        if (this._heartbeatTimer) {
            clearInterval(this._heartbeatTimer)
            this._heartbeatTimer = null
        }
        if (this._visibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._visibilityHandler)
            this._visibilityHandler = null
        }
    }

    _handleLockLost() {
        this._stopHeartbeat()
        this._lockToken = null
        if (this._onLockLost) {
            try { this._onLockLost() } catch (err) {
                console.warn('onLockLost handler threw:', err.message)
            }
        }
    }

    _handleSaveError(err) {
        if (this._onSaveError) {
            try { this._onSaveError(err) } catch { /* ignore */ }
        } else {
            console.warn('Memory save failed:', err.message)
        }
    }
}

function makeError(code, message, extras) {
    const err = new Error(message)
    err.code = code
    if (extras && typeof extras === 'object') Object.assign(err, extras)
    return err
}

export const _internal = { SAVE_DEBOUNCE_MS, HEARTBEAT_INTERVAL_MS }
